import type { CashFlowPeriod } from '../../../hooks/useReports';

interface Props {
  cf90: CashFlowPeriod;
  overdueTotal: number;
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d: string) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '—';

const RECURRENCE_PT: Record<string, string> = {
  monthly: 'Mensal', weekly: 'Semanal', annual: 'Anual', daily: 'Diário', biweekly: 'Quinzenal',
};

export const FutureCommitments = ({ cf90, overdueTotal, loading }: Props) => {
  if (loading) return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
      <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
    </div>
  );

  const expenses = cf90.items.filter(i => i.type === 'despesa');
  const recurring = expenses.filter(i => i.recurrence).sort((a, b) => a.date.localeCompare(b.date));
  const installments = expenses.filter(i => i.installments > 1 && !i.recurrence).sort((a, b) => a.date.localeCompare(b.date));

  const recurringTotal = recurring.reduce((s, i) => s + i.amount, 0);
  const installmentsTotal = installments.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Recorrências */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[14px] font-700 text-gray-900">Recorrências Futuras</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">Próximos 90 dias</p>
          </div>
          <div className="text-right">
            <p className="text-[16px] font-700 text-sky-600">{fmt(recurringTotal)}</p>
            <p className="text-[10px] text-gray-400">{recurring.length} lançamentos</p>
          </div>
        </div>
        {recurring.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400 text-[12px] gap-2">
            <i className="ri-repeat-line text-2xl text-gray-200" />
            Nenhuma recorrência prevista
          </div>
        ) : (
          <div className="space-y-2">
            {recurring.slice(0, 7).map(item => (
              <div key={item.id} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-sky-50 rounded-xl shrink-0">
                  <i className="ri-repeat-line text-sky-500 text-[13px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-gray-800 font-500 truncate">{item.description}</p>
                  <p className="text-[10px] text-gray-400">{fmtDate(item.date)} · {item.recurrence ? (RECURRENCE_PT[item.recurrence] ?? item.recurrence) : ''}</p>
                </div>
                <span className="text-[12px] font-700 text-gray-700 whitespace-nowrap shrink-0">{fmt(item.amount)}</span>
              </div>
            ))}
            {recurring.length > 7 && (
              <p className="text-[11px] text-gray-400 text-center py-1">+{recurring.length - 7} mais</p>
            )}
          </div>
        )}
      </div>

      {/* Parcelamentos */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[14px] font-700 text-gray-900">Parcelamentos Futuros</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">Próximos 90 dias</p>
          </div>
          <div className="text-right">
            <p className="text-[16px] font-700 text-violet-600">{fmt(installmentsTotal)}</p>
            <p className="text-[10px] text-gray-400">{installments.length} parcelas</p>
          </div>
        </div>
        {installments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400 text-[12px] gap-2">
            <i className="ri-price-tag-3-line text-2xl text-gray-200" />
            Nenhum parcelamento futuro
          </div>
        ) : (
          <div className="space-y-2">
            {installments.slice(0, 7).map(item => (
              <div key={item.id} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-violet-50 rounded-xl shrink-0">
                  <i className="ri-price-tag-3-line text-violet-500 text-[13px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-gray-800 font-500 truncate">{item.description}</p>
                  <p className="text-[10px] text-gray-400">{fmtDate(item.date)} · {item.installments}x</p>
                </div>
                <span className="text-[12px] font-700 text-gray-700 whitespace-nowrap shrink-0">{fmt(item.amount)}</span>
              </div>
            ))}
            {installments.length > 7 && (
              <p className="text-[11px] text-gray-400 text-center py-1">+{installments.length - 7} mais</p>
            )}
          </div>
        )}
      </div>

      {overdueTotal > 0 && (
        <div className="col-span-full bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl shrink-0">
            <i className="ri-alarm-warning-line text-red-500 text-[18px]" />
          </div>
          <div>
            <p className="text-[13px] font-700 text-red-700">Cobranças Vencidas</p>
            <p className="text-[12px] text-red-600 mt-0.5">Total de <strong>{fmt(overdueTotal)}</strong> em lançamentos vencidos ou atrasados. Regularize para evitar encargos.</p>
          </div>
        </div>
      )}
    </div>
  );
};
